import './block-options-dropdown-list.scss';

import {
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import { $setBlocksType } from '@lexical/selection';
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  LexicalEditor,
} from 'lexical';
import { useCallback, useEffect, useRef, useState } from 'react';

import { EditorBlockButton } from '../editor-block-button/editor-block-button';

export enum BlockType {
  PARAGRAPH = 'paragraph',
  NUMBER_LIST = 'number',
  BULLET_LIST = 'bullet',
  CHECK_LIST = 'check',
}

type BlockOptionsDropdownListProps = {
  editor: LexicalEditor;
  selected: BlockType;
  options?: BlockType[];
  toolbar: HTMLDivElement | null;
  onClose?: () => void;
};

export function BlockOptionsDropdownList({
  editor,
  selected,
  toolbar,
  options,
  onClose,
}: BlockOptionsDropdownListProps) {
  const dropDownRef = useRef<HTMLDivElement>(null);

  const [blockOptions, setBlockOptions] = useState<BlockType[]>([
    BlockType.PARAGRAPH,
    BlockType.BULLET_LIST,
    BlockType.NUMBER_LIST,
    BlockType.CHECK_LIST,
  ]);

  useEffect(() => {
    if (options) {
      setBlockOptions(options);
    }
  }, [options]);

  useEffect(() => {
    const dropDown = dropDownRef.current;

    if (toolbar !== null && dropDown !== null) {
      const { top, left } = toolbar.getBoundingClientRect();
      dropDown.style.top = `${top + 40}px`;
      dropDown.style.left = `${left}px`;
    }
  }, [dropDownRef, toolbar]);

  useEffect(() => {
    const dropDown = dropDownRef.current;

    if (dropDown !== null && toolbar !== null) {
      const handle = (event: MouseEvent) => {
        const { target } = event;

        if (
          target !== null &&
          !dropDown.contains(target as Node) &&
          !toolbar.contains(target as Node)
        ) {
          onClose?.();
        }
      };
      document.addEventListener('click', handle);

      return () => {
        document.removeEventListener('click', handle);
      };
    }
    return undefined;
  }, [dropDownRef, onClose, toolbar]);

  const formatList = useCallback(
    (type: BlockType) => {
      const commandMap: Record<
        BlockType,
        { add: () => void; remove: () => void }
      > = {
        [BlockType.PARAGRAPH]: {
          add: () => {
            editor.update(() => {
              const selection = $getSelection();
              if ($isRangeSelection(selection)) {
                $setBlocksType(selection, $createParagraphNode);
              }
            });
          },
          remove: () => {},
        },
        [BlockType.BULLET_LIST]: {
          add: () => {
            editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
          },
          remove: () => {
            editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
          },
        },
        [BlockType.NUMBER_LIST]: {
          add: () => {
            editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
          },
          remove: () => {
            editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
          },
        },
        [BlockType.CHECK_LIST]: {
          add: () => {
            editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined);
          },
          remove: () => {
            editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
          },
        },
      };

      const command = commandMap[type];
      if (selected !== type) {
        command.add();
      } else {
        command.remove();
      }
      onClose?.();
    },
    [editor, selected, onClose]
  );

  return (
    <div className="block-list-dropdown" ref={dropDownRef}>
      {blockOptions.map(key => (
        <EditorBlockButton
          wide
          active={selected === key}
          key={key}
          format={key}
          text={key}
          onClick={() => {
            formatList(key);
          }}
        />
      ))}
      {/* Object.entries(blockTypes).map(([key, value]) => (
        <EditorBlockButton
          wide
          active={selected === key}
          key={key}
          format={value}
          text={value}
          onClick={() => {
            formatList(key);
          }}
        />
      )) */}
    </div>
  );
}
